import { useTranslate } from '../../application/i18n/LocaleProvider';
import { formatTrackTime, trackProgress } from '../../domain/sound/soundtrack';
import trackSubtitle from './trackVersion';
import {
    EqualiserIcon,
    PauseIcon,
    PlayIcon,
    ShuffleIcon,
    SkipBackIcon,
    SkipForwardIcon,
} from '../icons/JukeboxIcons';

/**
 * What is playing, and the buttons that change it (SPEC-014 PR-14.3).
 *
 * Split out of `JukeboxSheet` because they are two things: this is a DECK — a
 * cover, a title, a position and five controls — and the sheet around it is the
 * shelf you pick from. The seam is also what keeps either file readable.
 *
 * THE SEEK BAR IS AN `input[type=range]`, which is the decision worth writing
 * down. A progress bar drawn as a div with a pointer handler is four lines
 * shorter and loses: keyboard stepping, the arrow keys, page-up, the value
 * announced, and the OS's own idea of what a slider feels like under a thumb.
 * This product's rule is that a gesture is never the only way to do something
 * (NFR-04), and here the platform simply gives us the other way.
 *
 * THERE IS NO VOLUME CONTROL, and that is a decision rather than an omission.
 * iOS accepts a write to `element.volume`, ignores it, and reads back the level
 * it already had — the output level belongs to the hardware buttons and to
 * nothing else. A slider that worked on a desktop and did nothing on the device
 * this product is designed for is worse than no slider, and the phone already
 * has a volume control on its side (SPEC-014 D-18).
 */
export default function JukeboxDeck({ jukebox }) {
    const t = useTranslate();
    const {
        track,
        elapsed,
        duration,
        isPlaying,
        isShuffled,
        toggle,
        next,
        previous,
        toggleShuffle,
        seekTo,
    } = jukebox;

    // The element's own duration once it has loaded, and the manifest's measured
    // one until then. Neither alone is enough: the element knows nothing before
    // the first byte arrives, and the manifest is right about every track on the
    // shelf including the seven nobody has pressed yet.
    const length = duration > 0 ? duration : (track?.seconds || 0);
    const subtitle = trackSubtitle(track, t);
    const progress = trackProgress({ seconds: elapsed, duration: length });

    return (
        <div className="jukebox__deck">
            <div className="jukebox__cover">
                {track?.cover ? (
                    <img
                        className="jukebox__cover-art"
                        src={track.cover.src}
                        srcSet={track.cover.srcSet}
                        /* The deck's own column, restated for the preload
                           parser. It said 120px for a 96px box on every screen
                           ever built, which cost nothing on a phone and was
                           simply wrong the moment the deck became a pane: from
                           1200 the cover is the largest picture on the screen,
                           and a `sizes` left behind would have fetched the 240
                           rung for a 376px stage.

                           The four steps mirror `.jukebox__deck` and
                           `.jukebox__cover` in `jukebox.css` — a 160px column in
                           the wide band, then the pane's width less its padding
                           at each split step. The `40vh` bound the sheet also
                           puts on the cover can only make the box smaller, so
                           these are ceilings and the over-fetch is the safe
                           direction. */
                        sizes="(min-width: 1920px) 376px, (min-width: 1440px) 352px, (min-width: 1200px) 312px, (min-width: 1024px) 160px, 96px"
                        /* Decorative: the title is printed beside it, and a
                           description of a neon car under a car's name would
                           announce the same thing twice. */
                        alt=""
                    />
                ) : (
                    <EqualiserIcon className="jukebox__cover-fallback" size={40} />
                )}
            </div>

            <div className="jukebox__now">
                <p className="jukebox__label">{t('jukebox.nowPlaying')}</p>
                <p className="jukebox__track-title">{track?.title}</p>

                {/* The version, as a subtitle under the name rather than a chip
                    beside it. Twelve titles arrived as twenty recordings, and
                    which take you are hearing is a fact about THIS row — it
                    belongs under the title the way a pressing is named under an
                    album's, not tacked onto the end of the name where it would
                    read as part of it. Absent for an original, which is most of
                    them. */}
                {subtitle ? <p className="jukebox__track-variant">{subtitle}</p> : null}

                <input
                    className="jukebox__seek"
                    type="range"
                    min="0"
                    max="1000"
                    step="1"
                    value={Math.round(progress * 1000)}
                    onChange={(event) => seekTo(Number(event.target.value) / 1000)}
                    /* Nothing to seek in until the element has its own duration,
                       and with `preload="none"` that arrives with the first
                       press of play. An enabled slider that sprang back to zero
                       every time it was dragged would be a control the reader
                       can feel is broken; disabled, it also leaves the tab
                       order, which is the honest answer for a keyboard. */
                    disabled={duration <= 0}
                    aria-label={t('jukebox.seek')}
                    /* The number a slider announces is its own 0-1000, which
                       means nothing to anybody. These two make it say the time
                       it actually represents. */
                    aria-valuetext={`${formatTrackTime(elapsed)} / ${formatTrackTime(length)}`}
                />

                <p className="jukebox__times">
                    <span>{formatTrackTime(elapsed)}</span>
                    <span>{formatTrackTime(length)}</span>
                </p>
            </div>

            <div className="jukebox__transport">
                <button
                    type="button"
                    className={`jukebox__control${isShuffled ? ' jukebox__control--on' : ''}`}
                    onClick={toggleShuffle}
                    aria-pressed={isShuffled}
                    aria-label={t(isShuffled ? 'jukebox.shuffleOn' : 'jukebox.shuffleOff')}
                >
                    <ShuffleIcon size={20} />
                </button>

                <button type="button" className="jukebox__control" onClick={previous} aria-label={t('jukebox.previous')}>
                    <SkipBackIcon size={20} />
                </button>

                {/* The one filled control in the sheet, and the only place the
                    heat ramp is spent here. A jukebox has exactly one primary
                    action and this is it. */}
                <button
                    type="button"
                    className="jukebox__play"
                    onClick={toggle}
                    aria-label={t(isPlaying ? 'jukebox.pause' : 'jukebox.play')}
                >
                    {isPlaying ? <PauseIcon size={24} /> : <PlayIcon size={24} />}
                </button>

                <button type="button" className="jukebox__control" onClick={next} aria-label={t('jukebox.next')}>
                    <SkipForwardIcon size={20} />
                </button>
            </div>

        </div>
    );
}
